'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Search, X, MapPin, Clock, Sparkles, TrendingUp, Gem, Compass, Users, Archive } from 'lucide-react';
import { editorialCards, type EditorialCard } from './editorialData';
import Icon from '@/components/ui/AppIcon';


const CATEGORY_FILTERS = [
  { id: 'all', label: 'All', icon: Sparkles },
  { id: 'Places', label: 'Places', icon: MapPin },
  { id: 'Objects', label: 'Objects', icon: Gem },
  { id: 'Experiences', label: 'Experiences', icon: Compass },
  { id: 'Markets', label: 'Markets', icon: TrendingUp },
  { id: 'People', label: 'People', icon: Users },
  { id: 'Collections', label: 'Collections', icon: Archive },
];

const EXCLUSIVITY_FILTERS = ['All', 'Singular', 'Rare', 'Exclusive'];

const TRENDING = [
  'Private Islands',
  'Rare Timepieces',
  'Secret Wine Estates',
  'Dragon Blood Forest',
  'Ottoman Manuscripts',
  'Whisky Casks',
];

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeExclusivity, setActiveExclusivity] = useState('All');
  const [results, setResults] = useState<EditorialCard[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const runSearch = useCallback((q: string, cat: string, excl: string) => {
    const lower = q.toLowerCase().trim();
    const filtered = editorialCards.filter((card) => {
      const matchesQuery =
        !lower ||
        card.title.toLowerCase().includes(lower) ||
        card.subtitle.toLowerCase().includes(lower) ||
        card.excerpt.toLowerCase().includes(lower) ||
        (card.location?.toLowerCase().includes(lower) ?? false);
      const matchesCat = cat === 'all' || card.category === cat;
      const matchesExcl = excl === 'All' || card.exclusivityLevel === excl;
      return matchesQuery && matchesCat && matchesExcl;
    });
    setResults(filtered);
  }, []);

  useEffect(() => {
    runSearch(query, activeCategory, activeExclusivity);
  }, [query, activeCategory, activeExclusivity, runSearch]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setQuery('');
      setActiveCategory('all');
      setActiveExclusivity('All');
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!open) return null;

  const showTrending = !query && activeCategory === 'all' && activeExclusivity === 'All';

  return (
    <div className="fixed inset-0 z-[200] flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-obsidian/90 backdrop-blur-md" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-2xl mx-auto mt-16 mx-4 sm:mx-auto px-4">
        {/* Search Input */}
        <div className="flex items-center gap-3 bg-[#1a1a1a] border border-[var(--gold)]/30 rounded-lg px-4 py-3.5 shadow-luxury-lg">
          <Search size={18} className="text-[var(--gold)] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search places, objects, experiences, people..."
            className="flex-1 bg-transparent text-cream text-base placeholder:text-muted-foreground outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-cream transition-colors">
              <X size={16} />
            </button>
          )}
          <button onClick={onClose} className="text-muted-foreground hover:text-cream transition-colors ml-1">
            <span className="text-xs border border-muted px-1.5 py-0.5 rounded text-muted-foreground">ESC</span>
          </button>
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-col gap-2">
          {/* Category chips */}
          <div className="flex gap-1.5 overflow-x-auto scroll-hidden">
            {CATEGORY_FILTERS.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-all ${
                    isActive
                      ? 'bg-[var(--gold)] text-obsidian border-[var(--gold)] font-semibold'
                      : 'bg-muted/20 border-muted text-muted-foreground hover:border-muted-foreground hover:text-cream'
                  }`}
                >
                  <Icon size={11} />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Exclusivity chips */}
          <div className="flex gap-1.5">
            {EXCLUSIVITY_FILTERS.map((excl) => (
              <button
                key={excl}
                onClick={() => setActiveExclusivity(excl)}
                className={`px-3 py-1 rounded-full text-xs border transition-all ${
                  activeExclusivity === excl
                    ? 'bg-cream/10 border-cream/40 text-cream font-semibold' :'border-muted/40 text-muted-foreground hover:border-muted hover:text-cream'
                }`}
              >
                {excl}
              </button>
            ))}
          </div>
        </div>

        {/* Results / Trending */}
        <div className="mt-4 bg-[#111] border border-muted/40 rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
          {showTrending ? (
            <div className="p-4">
              <p className="text-[10px] tracking-widest uppercase text-muted-foreground mb-3">Trending Searches</p>
              <div className="flex flex-wrap gap-2">
                {TRENDING.map((term) => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/20 border border-muted/40 rounded-full text-xs text-muted-foreground hover:text-cream hover:border-muted transition-colors"
                  >
                    <TrendingUp size={10} />
                    {term}
                  </button>
                ))}
              </div>
              <div className="mt-5">
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground mb-3">All Discoveries ({editorialCards.length})</p>
                <div className="flex flex-col gap-0">
                  {editorialCards.slice(0, 5).map((card) => (
                    <ResultRow key={card.id} card={card} onClose={onClose} />
                  ))}
                </div>
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="py-12 text-center">
              <Search size={28} className="text-muted mx-auto mb-3" />
              <p className="text-cream text-sm font-medium">No discoveries found</p>
              <p className="text-muted-foreground text-xs mt-1">Try different keywords or adjust filters</p>
            </div>
          ) : (
            <div className="p-3">
              <p className="text-[10px] tracking-widest uppercase text-muted-foreground mb-3 px-1">
                {results.length} {results.length === 1 ? 'Discovery' : 'Discoveries'} Found
              </p>
              <div className="flex flex-col gap-0">
                {results.map((card) => (
                  <ResultRow key={card.id} card={card} onClose={onClose} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ card, onClose }: { card: EditorialCard; onClose: () => void }) {
  return (
    <Link
      href={`/article?slug=${card.id}`}
      onClick={onClose}
      className="flex items-center gap-3 px-3 py-3 rounded hover:bg-muted/20 transition-colors group border-b border-muted/20 last:border-0"
    >
      <div className="relative w-12 h-9 flex-shrink-0 rounded overflow-hidden">
        <AppImage src={card.image} alt={card.alt} fill className="object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-cream font-medium leading-tight line-clamp-1 group-hover:text-[var(--gold)] transition-colors">
          {card.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[var(--gold)] font-semibold tracking-wide">{card.category}</span>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-[10px] text-muted-foreground">{card.exclusivityLevel}</span>
          {card.location && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <MapPin size={8} />
                {card.location}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0">
        <Clock size={9} />
        {card.readTime}m
      </div>
    </Link>
  );
}
